import { create } from 'zustand'
import { trackLogin, trackLoginFailed, trackLogout, setAnalyticsUser } from '../../../lib/analytics'
import { setGlitchTipUser } from '../../../lib/glitchtip'
import axiosInstance, { API_BASE_URL } from '../../../lib/axios'
import { isApiError } from '../../../lib/apiError'
import { API_ENDPOINTS } from '../../../constants/api'
import { usePropertyStore } from '../../../store/propertyStore'
import type { User, LoginResponse } from '../../../types/api.types'

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  isLogoutModalOpen: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<{ success: boolean; error?: string }>
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string; error?: string }>
  resetPassword: (token: string, newPassword: string) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  restoreSession: () => Promise<void>
  openLogoutModal: () => void
  closeLogoutModal: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
  isLogoutModalOpen: false,

  openLogoutModal: () => set({ isLogoutModalOpen: true }),
  closeLogoutModal: () => set({ isLogoutModalOpen: false }),

  login: async (email: string, password: string) => {
    // Use fetch directly so we always see the real HTTP status code,
    // even when CORS strips error-response headers from cross-origin requests.
    let res: Response
    let body: Record<string, unknown> = {}
    try {
      res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH_LOGIN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      body = await res.json().catch(() => ({}))
    } catch {
      void trackLoginFailed({ reason: 'error' })
      return { success: false, error: 'Unable to connect. Please check your internet connection.' }
    }

    if (!res.ok) {
      const status = res.status
      const detail = typeof body?.detail === 'string' ? body.detail : undefined
      void trackLoginFailed({
        status,
        reason: status === 401 ? 'invalid_credentials' : status === 403 ? 'forbidden' : 'error',
      })

      let message: string
      if (status === 401) {
        message = 'Invalid email or password.'
      } else if (status === 403) {
        message = detail ?? 'Your account has been disabled. Please contact your administrator.'
      } else if (status === 429) {
        const retryAfter = res.headers.get('retry-after')
        const seconds = retryAfter ? Math.max(1, Math.ceil(Number(retryAfter))) : null
        message = seconds
          ? `Too many login attempts. Please try again in ${seconds}s.`
          : 'Too many login attempts. Please wait and try again.'
      } else if (status >= 500) {
        message = detail ?? 'Server error. Please try again later.'
      } else {
        message = detail ?? 'Login failed. Please try again.'
      }

      return { success: false, error: message }
    }

    try {
      const loginData = body as unknown as LoginResponse
      sessionStorage.setItem('auth_token', loginData.access_token)
      if (loginData.refresh_token) {
        sessionStorage.setItem('auth_refresh_token', loginData.refresh_token)
      }
      if (loginData.expires_at) {
        sessionStorage.setItem('auth_expires_at', loginData.expires_at)
      }

      const { data: user } = await axiosInstance.get<User>(API_ENDPOINTS.AUTH_CURRENT_USER)
      sessionStorage.setItem('auth_user', JSON.stringify(user))
      // Signing in starts clean. A property left over in sessionStorage from an
      // earlier sign-in is not this user's choice, so it does not carry over.
      usePropertyStore.getState().reset()
      set({ user, token: loginData.access_token, isAuthenticated: true })
      void trackLogin(user)
      setGlitchTipUser(user)

      return { success: true }
    } catch (err: unknown) {
      const apiErr = isApiError(err) ? err : null
      return { success: false, error: apiErr?.message ?? 'Login failed. Please try again.' }
    }
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    try {
      await axiosInstance.post(API_ENDPOINTS.AUTH_CHANGE_PASSWORD, {
        current_password: currentPassword,
        new_password: newPassword,
      })
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('auth_user')
      sessionStorage.removeItem('auth_expires_at')
      sessionStorage.removeItem('auth_refresh_token')
      set({ user: null, token: null, isAuthenticated: false, isLogoutModalOpen: false })
      return { success: true }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string }
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Unable to change password.',
      }
    }
  },

  forgotPassword: async (email: string) => {
    try {
      const { data } = await axiosInstance.post<{ message?: string }>(
        API_ENDPOINTS.AUTH_FORGOT_PASSWORD,
        { email },
      )
      return {
        success: true,
        message:
          data?.message ||
          'If an account with that email exists, a password reset link has been sent.',
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string }
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Unable to process request.',
      }
    }
  },

  resetPassword: async (token: string, newPassword: string) => {
    try {
      await axiosInstance.post(API_ENDPOINTS.AUTH_RESET_PASSWORD, {
        token,
        new_password: newPassword,
      })
      return { success: true }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string }
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Unable to reset password.',
      }
    }
  },

  logout: async () => {
    const { user } = useAuthStore.getState()
    try {
      await axiosInstance.post(API_ENDPOINTS.AUTH_LOGOUT, {})
    } catch {
      // swallow
    }
    void trackLogout(user)
    setGlitchTipUser(null)
    sessionStorage.removeItem('auth_token')
    sessionStorage.removeItem('auth_user')
    sessionStorage.removeItem('auth_expires_at')
    sessionStorage.removeItem('auth_refresh_token')
    usePropertyStore.getState().reset()
    set({ user: null, token: null, isAuthenticated: false, isLogoutModalOpen: false })
  },

  restoreSession: async () => {
    const storedToken = sessionStorage.getItem('auth_token')

    if (!storedToken) {
      set({ isLoading: false })
      return
    }

    try {
      const { data: user } = await axiosInstance.get<User>(API_ENDPOINTS.AUTH_CURRENT_USER)
      sessionStorage.setItem('auth_user', JSON.stringify(user))
      set({ user, token: storedToken, isAuthenticated: true, isLoading: false })
      void setAnalyticsUser(user)
      setGlitchTipUser(user)
    } catch {
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('auth_user')
      sessionStorage.removeItem('auth_expires_at')
      sessionStorage.removeItem('auth_refresh_token')
      set({ isLoading: false })
    }
  },
}))
